// Per-card graded (PSA / BGS / CGC) price page for one Palworld chase printing.
//
// The raw card page lives at /cards/<code>; this page is certified copies only:
// live slab asking range, the premium over raw as a multiple, grading-decision
// maths and population-report links. Unknown or non-chase codes redirect to the
// graded hub instead of soft-404ing.
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { DefaultRouteError } from "@/components/RouteFallbacks";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";
import { CardZoom } from "@/components/CardZoom";
import { RarityBadge } from "@/components/RarityBadge";
import { RatingStars } from "@/components/CardRating";
import { GRADED_MULTIPLE, gradedFaq, gradedPath, resolveGraded } from "@/lib/graded-pages";
import { GRADING_SOURCES, SOURCE_BY_ID, formatMoney } from "@/lib/prices-shared";
import { SET_NAMES } from "@/lib/tcg-cards";
import { useCardPrice } from "@/lib/use-card-prices";

export const Route = createFileRoute("/graded-card-prices/$code")({
  beforeLoad: ({ params }) => {
    const card = resolveGraded(params.code);
    if (!card) throw redirect({ to: "/graded-card-prices", replace: true });
    if (card.code.toUpperCase() !== params.code.toUpperCase())
      throw redirect({ to: "/graded-card-prices/$code", params: { code: card.code.toUpperCase() }, replace: true });
  },
  loader: ({ params }) => ({ card: resolveGraded(params.code)! }),
  head: ({ loaderData }) => {
    if (!loaderData)
      return { meta: [{ title: "Graded card unavailable — Pal Stars" }, { name: "robots", content: "noindex" }] };
    const c = loaderData.card;
    const url = `https://palstars.com${gradedPath(c.code)}`;
    const title = `${c.code} ${c.name} PSA & BGS Price`;
    const desc = `${c.code} ${c.name} graded price: live PSA, BGS and CGC slab asking range, the premium over the raw ${c.rarity} market price, and population-report links.`.slice(
      0,
      158,
    );
    return {
      meta: [
        { title: `${title} | Pal Stars` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(c.image
          ? [
              { property: "og:image", content: c.image },
              { name: "twitter:image", content: c.image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbSchema([
          ["Home", "/"],
          ["Graded Card Prices", "/graded-card-prices"],
          [`${c.code} graded`, gradedPath(c.code)],
        ]),
        webPageSchema({ name: `${c.code} ${c.name} graded price`, description: desc, path: gradedPath(c.code) }),
        faqSchema(gradedFaq(c), url),
      ],
    };
  },
  component: GradedCardPage,
  errorComponent: DefaultRouteError,
});

const formatMoney2 = (n: number) => formatMoney(n, "USD");

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="glass rounded-2xl p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-extrabold tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function GradedCardPage() {
  const { card: c } = Route.useLoaderData();
  const row = useCardPrice(c.code);
  const setName = SET_NAMES[c.set] ?? c.set;
  const mult = GRADED_MULTIPLE[c.rarity] ?? "a multiple of raw";
  const graded = row?.gradedAvg ?? null;
  const raw = row && row.market > 0 ? row.market : null;
  const premium = graded && raw ? Math.round((graded / raw) * 10) / 10 : null;
  const gradedSearch = SOURCE_BY_ID["ebay-graded"]?.card(c);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">
        {c.code} {c.name} — PSA & BGS Graded Price
      </h1>
      <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
        A graded {c.name} ({c.code}, {c.rarity} from {setName}) trades at {mult} in a top grade, because a certified
        gem-mint copy is far scarcer than a raw one. The certified asking range below is read from live slab listings
        only — PSA, BGS and CGC — and sits next to the raw market price so the premium is visible as a multiple.
        Certified quotes never feed the raw price or the Pal Stars index.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <RarityBadge code={c.code} />
        <RatingStars code={c.code} />
      </div>

      <AffiliateDisclosure className="mt-6" />

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,340px)_1fr]">
        <div>
          {c.image && (
            <CardZoom
              src={c.image}
              alt={`${c.name} Palworld TCG card ${c.code}`}
              caption={`${c.code} — ${c.rarity} · ${setName}`}
              cardCode={c.code}
              cardName={c.name}
            />
          )}
        </div>

        <div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat
              label="Graded (slab) asking"
              value={graded != null ? formatMoney2(graded) : "—"}
              hint={graded != null ? `${row?.gradedCount ?? 0} certified listings sampled` : "No certified listing tracked today"}
            />
            <Stat label="Raw market" value={raw != null ? formatMoney2(raw) : "—"} hint="Raw single, English printing" />
            <Stat
              label="Graded premium"
              value={premium != null ? `${premium}x raw` : "—"}
              hint={`Typical for ${c.rarity}: ${mult}`}
            />
          </div>

          <section className="glass mt-6 rounded-2xl p-5">
            <h2 className="text-xl font-bold">Should you grade this card?</h2>
            <p className="aeo-answer mt-2 text-sm text-muted-foreground">
              Grade {c.code} only when the copy is gem-mint and the graded price minus the raw price clearly exceeds
              grading plus shipping both ways. Check centring first, then corners under a light, then surface at an
              angle: a copy that grades 9 instead of 10 usually returns less than the submission cost on a modern
              release. Submit in a multi-card order to spread postage, and photograph the card before it ships.
            </p>
          </section>

          <section className="glass mt-6 rounded-2xl p-5">
            <h2 className="text-xl font-bold">Buy a graded {c.name}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Certified slab listings for the exact printed code {c.code}. Compare the slab label grade and the
              population at that grade before paying the premium.
            </p>
            {gradedSearch && (
              <a
                href={gradedSearch}
                target="_blank"
                rel="nofollow ugc noopener noreferrer"
                className="mt-3 inline-flex items-center rounded-full bg-[#0064D2] px-3 py-1.5 text-xs font-semibold text-white"
              >
                Buy graded on eBay
              </a>
            )}
          </section>

          <section className="glass mt-6 rounded-2xl p-5">
            <h2 className="text-xl font-bold">Population reports</h2>
            <ul className="mt-2 space-y-2 text-sm">
              {GRADING_SOURCES.map((g) => (
                <li key={g.id}>
                  <a
                    href={g.pop}
                    target="_blank"
                    rel="nofollow ugc noopener noreferrer"
                    className="font-semibold text-primary hover:underline"
                  >
                    {g.company}
                  </a>{" "}
                  <span className="text-muted-foreground">— {g.scale}</span>
                </li>
              ))}
            </ul>
          </section>

          <p className="mt-6 text-sm">
            Raw price history and printings:{" "}
            <Link to="/cards/$code" params={{ code: c.base.toUpperCase() }} className="text-primary hover:underline">
              {c.base} card page
            </Link>{" "}
            ·{" "}
            <Link to="/graded-card-prices" className="text-primary hover:underline">
              all graded price pages
            </Link>
          </p>
        </div>
      </div>

      <InternalLinkBlock path="/prices" />
      <FaqSection items={gradedFaq(c)} />
    </div>
  );
}
